import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Sparkles, Zap, Shield, Rocket } from "lucide-react";
import { SERVICES } from "@/lib/services-data";
import Seo from "@/components/Seo";

const HOME_JSONLD = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://spinlabs.in/#webpage",
    name: "Spin Labs — Software, AI, Mobile & SaaS Studio",
    url: "https://spinlabs.in/",
    description:
      "Full-stack software studio building AI-native websites, mobile apps, custom AI systems, SaaS platforms, and growth engines for ambitious teams.",
    inLanguage: "en",
    isPartOf: { "@id": "https://spinlabs.in/#website" },
  },
  {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "Spin Labs",
    url: "https://spinlabs.in",
    image: "https://spinlabs.in/og-cover.jpg",
    priceRange: "$$$",
    address: { "@type": "PostalAddress", addressLocality: "Delhi", addressCountry: "IN" },
    areaServed: "Worldwide",
    serviceType: [
      "Mobile App Development",
      "AI-Powered Website",
      "Custom AI System",
      "Software Development",
      "SEO Engineering",
      "Marketing",
      "SaaS Platform",
    ],
  },
];

const STATS = [
  { k: "120+", v: "Projects Shipped" },
  { k: "40M+", v: "Users Reached" },
  { k: "8 yrs", v: "Average Team XP" },
  { k: "4.9/5", v: "Client Rating" },
];

const LOGOS = ["ACME", "LUMEN", "ORBIT", "NOVA", "PIXEL", "FORGE", "AXION", "HELIX"];

const PROCESS = [
  { n: "01", title: "Discover", desc: "Deep-dive into goals, users, market. We map the problem before touching a pixel." },
  { n: "02", title: "Design", desc: "Wireframes, prototypes, motion studies. You see it work before we write a line." },
  { n: "03", title: "Build", desc: "Two-week sprints with live demos, CI/CD from day one, and zero dark launches." },
  { n: "04", title: "Launch", desc: "Global rollout, performance tuning, launch comms, and post-launch iteration." },
];

const TESTIMONIALS = [
  { q: "They ship like founders, not contractors. We had a working product in weeks, not quarters.", a: "Early client", r: "AI healthcare startup" },
  { q: "The rebuild paid for itself within a quarter. Cleaner code, faster pages, fewer on-call pages.", a: "Founding team", r: "B2B SaaS platform" },
  { q: "Small, senior, ruthlessly focused. They fixed problems we didn't know we had.", a: "Engineering lead", r: "Fintech product" },
];

function FadeUp({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.3]);

  return (
    <div data-testid="home-page">
      <Seo
        path="/"
        description="Spin Labs is a full-stack software studio building AI-native websites, mobile apps, custom AI systems, SaaS platforms, and growth engines for ambitious teams."
        keywords="software development, AI website, mobile app development, SaaS development, custom AI, AI agency, marketing automation, SEO, Spin Labs"
        jsonLd={HOME_JSONLD}
      />
      {/* ===== HERO ===== */}
      <section ref={heroRef} className="relative min-h-[85svh] lg:min-h-[100svh] flex items-center pt-24 lg:pt-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg-lg opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050505]" />
        <div className="spotlight w-[400px] md:w-[600px] h-[400px] md:h-[600px] bg-[#00F0FF]/20 top-10 -left-40" />
        <div className="spotlight w-[500px] md:w-[700px] h-[500px] md:h-[700px] bg-[#FF00FF]/15 bottom-0 -right-40" />

        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full pt-12 pb-20 lg:pt-16 lg:pb-28">
          <FadeUp>
            <div className="tag-chip mb-6 md:mb-8" data-testid="hero-chip">
              <Sparkles className="w-3 h-3" />
              Software · AI · Marketing · SaaS
            </div>
          </FadeUp>

          <FadeUp delay={0.1}>
            <h1 data-testid="hero-title" className="font-display text-[40px] sm:text-6xl lg:text-[112px] font-black tracking-[-0.04em] leading-[0.95] lg:leading-[0.9] text-balance">
              We build the <span className="gradient-text glitch" data-text="future">future</span><br />
              of software.
            </h1>
          </FadeUp>

          <FadeUp delay={0.2}>
            <p className="mt-6 md:mt-8 max-w-2xl text-sm sm:text-base lg:text-lg text-white/60 leading-relaxed">
              Spin Labs is a full-stack studio crafting intelligent mobile apps, AI-native websites,
              custom AI systems, SaaS platforms, and growth engines for companies that refuse to be average.
            </p>
          </FadeUp>

          <FadeUp delay={0.3}>
            <div className="mt-8 md:mt-10 flex flex-col sm:flex-row flex-wrap gap-3 md:gap-4">
              <Link to="/contact" data-testid="hero-cta-primary" className="btn-primary !px-6 !py-3.5 sm:!px-8 sm:!py-4 !text-sm sm:!text-base group">
                Start a Project
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Link>
              <Link to="/work" data-testid="hero-cta-secondary" className="btn-secondary !px-6 !py-3.5 sm:!px-8 sm:!py-4 !text-sm sm:!text-base">
                View Our Work
              </Link>
            </div>
          </FadeUp>

          {/* Floating 3D orb */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.4 }}
            className="absolute right-4 lg:right-12 top-32 hidden lg:block pointer-events-none"
          >
            <div className="relative w-80 h-80">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#00F0FF] via-[#FF00FF] to-[#00F0FF] opacity-30 blur-3xl animate-pulse-glow" />
              <div className="absolute inset-8 rounded-full border border-[#00F0FF]/30 animate-float" />
              <div className="absolute inset-16 rounded-full border border-[#FF00FF]/30 animate-float" style={{ animationDelay: "1s" }} />
              <div className="absolute inset-24 rounded-full bg-gradient-to-br from-[#00F0FF]/80 to-[#FF00FF]/80 blur-2xl animate-float" style={{ animationDelay: "0.5s" }} />
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Stats bar */}
      <section className="relative border-y border-white/5 glass z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/5">
          {STATS.map((s, i) => (
            <div key={i} className="px-3 sm:px-4 py-5 sm:py-6 text-center" data-testid={`hero-stat-${i}`}>
              <div className="font-display text-xl sm:text-2xl lg:text-3xl font-black text-white">{s.k}</div>
              <div className="font-mono-neo text-[9px] sm:text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.2em] text-white/50 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
      </section>

      {/* ===== LOGO MARQUEE ===== */}
      <section className="py-14 border-y border-white/5 overflow-hidden bg-[#070709]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-6">
          <div className="font-mono-neo text-xs uppercase tracking-[0.3em] text-white/40 text-center">
            Trusted by teams building tomorrow
          </div>
        </div>
        <div className="relative">
          <div className="flex gap-16 animate-marquee whitespace-nowrap">
            {[...LOGOS, ...LOGOS, ...LOGOS].map((l, i) => (
              <div key={i} className="font-display text-3xl font-black text-white/30 hover:text-[#00F0FF] transition-colors tracking-[0.2em]">
                {l}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SERVICES PREVIEW ===== */}
      <section className="relative py-24 lg:py-32 overflow-hidden" data-testid="services-section">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-16">
            <div className="max-w-2xl">
              <div className="tag-chip mb-4">What We Do</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-balance">
                Seven disciplines.<br />One obsession: <span className="gradient-text">shipping.</span>
              </h2>
            </div>
            <Link to="/services" className="btn-secondary group">
              Explore Services
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              const span = i === 0 ? "lg:col-span-2" : "";
              return (
                <FadeUp key={s.id} delay={i * 0.05} className={span}>
                  <div
                    data-testid={`service-card-${s.id}`}
                    className="card-hover group relative rounded-2xl p-8 h-full overflow-hidden"
                  >
                    <div
                      className="absolute -top-20 -right-20 w-60 h-60 rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity"
                      style={{ background: s.color }}
                    />
                    <div className="relative z-10">
                      <div
                        className="inline-flex w-14 h-14 rounded-xl items-center justify-center mb-6 border"
                        style={{ borderColor: `${s.color}50`, background: `${s.color}10` }}
                      >
                        <Icon className="w-7 h-7" style={{ color: s.color }} />
                      </div>
                      <div className="font-mono-neo text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: s.color }}>
                        {s.tag}
                      </div>
                      <h3 className="font-display text-2xl font-bold mb-3">{s.title}</h3>
                      <p className="text-sm text-white/60 leading-relaxed mb-6">{s.desc}</p>
                      <Link to="/services" className="inline-flex items-center gap-2 text-sm font-medium text-white/80 hover:text-[#00F0FF]">
                        Learn more <ArrowUpRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== PROCESS ===== */}
      <section className="relative py-24 lg:py-32 bg-[#07070A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <FadeUp>
            <div className="tag-chip mb-4">How We Work</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-balance max-w-3xl">
              A process obsessed with <span className="neon-text">momentum.</span>
            </h2>
          </FadeUp>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS.map((p, i) => (
              <FadeUp key={p.n} delay={i * 0.08}>
                <div className="relative border border-white/10 rounded-2xl p-8 h-full hover:border-[#00F0FF]/40 transition-colors">
                  <div className="font-mono-neo text-xs text-[#00F0FF] mb-4">{p.n}</div>
                  <h3 className="font-display text-xl font-bold mb-3">{p.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{p.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHY US ===== */}
      <section className="relative py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <FadeUp>
              <div className="tag-chip mb-4">Why Spin Labs</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-balance">
                Built for teams who want <span className="gradient-text">velocity</span> without chaos.
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="mt-6 text-white/60 leading-relaxed max-w-xl">
                We pair a senior product squad with AI-first tooling to deliver the work of a 30-person agency
                with the intimacy of a 3-person team. No juniors on your project. No deck fluff. Just shipped work.
              </p>
            </FadeUp>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                { i: Zap, t: "10x Faster", d: "AI-native workflows" },
                { i: Shield, t: "Battle-Tested", d: "8+ years shipping" },
                { i: Rocket, t: "Ownership", d: "You own everything" },
              ].map((b, i) => (
                <FadeUp key={i} delay={0.1 + i * 0.1}>
                  <div className="p-5 border border-white/10 rounded-xl">
                    <b.i className="w-5 h-5 text-[#00F0FF] mb-3" />
                    <div className="font-display font-bold mb-1">{b.t}</div>
                    <div className="text-xs text-white/50 font-mono-neo uppercase tracking-widest">{b.d}</div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>

          <FadeUp delay={0.2}>
            <div className="relative aspect-square rounded-3xl overflow-hidden border border-white/10">
              <img
                src="https://images.unsplash.com/photo-1764268602042-88b05a211378?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMG5lb24lMjBnbG93aW5nJTIwc2hhcGVzJTIwZGFya3xlbnwwfHx8fDE3NzY1MjczMTF8MA&ixlib=rb-4.1.0&q=85"
                alt="Spin Labs abstract gradient"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-black/20 to-transparent" />
              <div className="scanline" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="glass rounded-xl p-5">
                  <div className="font-mono-neo text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] mb-1">LIVE</div>
                  <div className="font-display font-bold">Currently shipping: 4 products · 9 teams</div>
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="relative py-24 lg:py-32 bg-[#07070A] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <FadeUp>
            <div className="tag-chip mb-4">Clients</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-balance max-w-3xl">
              Quiet wins from the teams we've shipped with.
            </h2>
          </FadeUp>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <FadeUp key={i} delay={i * 0.1}>
                <figure className="h-full p-8 border border-white/10 rounded-2xl bg-[#0A0A0C] relative overflow-hidden">
                  <div className="absolute -top-10 -left-10 text-8xl font-display text-[#00F0FF]/10">"</div>
                  <blockquote className="relative text-white/80 leading-relaxed">{t.q}</blockquote>
                  <figcaption className="relative mt-6 pt-6 border-t border-white/10">
                    <div className="font-bold">{t.a}</div>
                    <div className="text-xs text-white/50 font-mono-neo uppercase tracking-widest">{t.r}</div>
                  </figcaption>
                </figure>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
